/**
 * Animated Number Component
 * Smoothly animates number changes with counting effect
 */

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedNumberProps {
  value: number
  duration?: number
  decimals?: number
  prefix?: string
  suffix?: string
  className?: string
  formatOptions?: Intl.NumberFormatOptions
}

export function AnimatedNumber({
  value,
  duration = 500,
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
  formatOptions,
}: AnimatedNumberProps) {
  const [displayValue, setDisplayValue] = useState(value)
  const previousValue = useRef(value)
  const animationRef = useRef<number>()

  useEffect(() => {
    const startValue = previousValue.current
    const endValue = value
    const startTime = performance.now()

    // Easing function (ease-out cubic)
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeOutCubic(progress)

      const currentValue = startValue + (endValue - startValue) * easedProgress
      setDisplayValue(currentValue)

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setDisplayValue(endValue)
        previousValue.current = endValue
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [value, duration])

  const formattedValue = formatOptions
    ? new Intl.NumberFormat('en-US', formatOptions).format(displayValue)
    : displayValue.toFixed(decimals)

  return (
    <span className={cn('tabular-nums', className)}>
      {prefix}
      {formattedValue}
      {suffix}
    </span>
  )
}

// Compact number formatter (e.g., 1.2K, 3.4M)
interface CompactNumberProps {
  value: number
  className?: string
  animate?: boolean
}

export function CompactNumber({ value, className, animate = true }: CompactNumberProps) {
  const formatCompact = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`
    }
    return num.toLocaleString()
  }

  if (animate) {
    return (
      <AnimatedNumber
        value={value}
        className={className}
        formatOptions={{ notation: 'compact', maximumFractionDigits: 1 }}
      />
    )
  }

  return <span className={cn('tabular-nums', className)}>{formatCompact(value)}</span>
}

// Percentage with optional trend indicator
interface PercentageProps {
  value: number
  showSign?: boolean
  showTrend?: boolean
  className?: string
  animate?: boolean
}

export function Percentage({
  value,
  showSign = true,
  showTrend = false,
  className,
  animate = true,
}: PercentageProps) {
  const isPositive = value > 0
  const isNegative = value < 0

  const colorClass = isPositive
    ? 'text-success'
    : isNegative
    ? 'text-error'
    : 'text-text-secondary'

  const sign = showSign && isPositive ? '+' : ''

  return (
    <span className={cn('inline-flex items-center gap-1 tabular-nums', colorClass, className)}>
      {showTrend && (
        <svg
          className={cn('w-3 h-3', isNegative && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      )}
      {animate ? (
        <AnimatedNumber value={value} decimals={1} prefix={sign} suffix="%" />
      ) : (
        <span>
          {sign}
          {value.toFixed(1)}%
        </span>
      )}
    </span>
  )
}

// Progress ring with animated fill
interface ProgressRingProps {
  progress: number // 0-100
  size?: number
  strokeWidth?: number
  className?: string
  showValue?: boolean
  color?: string
}

export function ProgressRing({
  progress,
  size = 48,
  strokeWidth = 4,
  className,
  showValue = true,
  color = 'stroke-primary',
}: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (progress / 100) * circumference

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-elevated"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={cn(color, 'transition-all duration-500 ease-out')}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      {showValue && (
        <span className="absolute text-xs font-medium text-text-primary tabular-nums">
          {Math.round(progress)}%
        </span>
      )}
    </div>
  )
}
